<template>
  <div class="button-download-pdf">
    <div class="login text-center">
      <h2 class="dialogTitle">ログイン / 会員登録</h2>
      <p class="dialogSubLead">
        アカウント情報を入力してログイン・会員登録してください。
      </p>

      <div class="login_formBlock account_login">
        <p class="fieldLabel">メールアドレス</p>
        <el-input
          v-model="mailLogin.email"
          type="email"
          autocomplete
          name="mail-link-input"
          :class="'formmb'"
          placeholder="yagish@example.com"
          :disabled="loginEmailSending"
        />
        <p class="fieldLabel">パスワード</p>
        <el-input
          v-model="mailLogin.passwd"
          type="password"
          autocomplete
          name="mail-link-input"
          :class="'formmb'"
          placeholder="パスワードを入力してください"
          :disabled="loginEmailSending"
        />
        <p class="loginLead" v-if="emailExists !== true">
          ※初めてご利用の場合は、半角英小文字・大文字・数字を組合せて8〜20字で入力してください。記号も使用できます。
        </p>
        <template v-if="emailExists !== true">
          <p class="fieldLabel">パスワード（再入力）</p>
          <el-input
            v-model="mailLogin.passwdConfirm"
            type="password"
            autocomplete
            name="mail-link-input"
            :class="'formmb'"
            placeholder="確認のためパスワードを再入力"
            :disabled="loginEmailSending"
          />
        </template>

        <p class="agreeLine">
          <el-checkbox v-model="isAgree">同意する</el-checkbox>
          <span class="termLead"
            >株式会社Yagishの<a href="/terms" target="_blank">利用規約</a>、<a
              href="https://www.yagish.jp/privacy_policy/"
              target="_blank"
              >プライバシーポリシー</a
            >に同意の上、ログイン（会員登録）してください。</span
          >
        </p>

        <el-button
          type="primary"
          id="account_login"
          class="account_loginbtn"
          :plain="isPlainButton"
          @click="submitIdPw"
          v-loading="loginEmailSending"
          >{{ submitButtonLabel }}</el-button
        >

        <a href="/repasswd" class="pw-change">パスワードを忘れた方はこちら</a>
        <a
          href="#"
          class="pw-change"
          v-if="appFlag != 'b' && !showMailLink"
          @click.prevent="showMailLink = true"
          >メールでログインリンクを送る</a
        >
      </div>

      <div
        class="login_formBlock maillink_login"
        v-if="appFlag != 'b' && showMailLink"
      >
        <p class="loginLead">
          メールに届いたリンクをクリックするだけでかんたんにログインできます。
        </p>
        <el-form
          ref="mailLoginForm"
          :model="mailLogin"
          :rules="mailLoginRules"
          @submit.native.prevent
        >
          <el-form-item :required="true" prop="email">
            <el-input
              v-model="mailLogin.email"
              type="email"
              autocomplete
              name="mail-link-input"
              :class="'formmb'"
              placeholder="yagish@example.com"
              :disabled="loginEmailSending"
            />
          </el-form-item>
          <el-button
            type="primary"
            id="maillink_login"
            class="button-block"
            :plain="isPlainButton"
            @click="mailSubmit"
            v-loading="loginEmailSending"
            >メールでリンクを送る</el-button
          >
        </el-form>
      </div>

      <p class="orDivider"><span>またはソーシャルアカウントで</span></p>
      <div class="login_formBlock social_login">
        <div class="social_loginlist">
          <el-button
            id="google-login"
            class="social_loginitem google-login"
            @click="signInGoogle()"
            title="Googleアカウントでログイン"
          />
          <button
            v-if="appFlag != 'b'"
            id="line-login"
            class="social_loginitem line-login"
            @click="signInLine()"
            title="LINEでログイン"
          />
          <button
            id="apple-login"
            class="social_loginitem apple-login"
            @click="signInApple()"
            title="appleIDでログイン"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  getCurrentInstance,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch
} from 'vue'
import { useDebounceFn } from '@vueuse/core'
import localforage from 'localforage'
import { useAuth } from '@/composables/useAuth'
import { useSite } from '@/composables/useSite'
import { emitter } from '@/plugins/emitter'
import { stashOfferChatRedirectUrl } from '@/utils/auth/offerChatRedirect'

const SING_IN_MAIL_SEND = 'sing-in-mail-send'
/** authMethods は `sing-in-mail-fail` を emit（旧購読名 failed との差は forward で吸収） */
const SING_IN_MAIL_FAIL = 'sing-in-mail-fail'

defineProps({
  isPlainButton: { type: Boolean, default: false }
})

type HostVm = {
  $message: (opts: Record<string, unknown>) => void
}

type MailLoginForm = {
  resetFields: () => void
  validate: (cb: (valid: boolean) => void) => void
}

type BasicData = { email?: string }

const vm = getCurrentInstance()!.proxy as unknown as HostVm
const { getClient } = useAuth()
const { appFlag } = useSite()

const loginEmailSending = ref(false)
const isAgree = ref(true)
const showMailLink = ref(false)
const emailExists = ref<boolean | null>(null)
const mailLoginForm = ref<MailLoginForm | null>(null)
const mailLogin = reactive({
  email: '',
  passwd: '',
  passwdConfirm: ''
})

const mailLoginRules = {
  email: [
    {
      required: true,
      message: 'メールアドレスをいれてね',
      trigger: 'blur'
    },
    {
      required: true,
      type: 'email',
      message: 'メールアドレスをいれてね',
      trigger: 'blur'
    }
  ]
}

const submitButtonLabel = computed(() => {
  if (emailExists.value === true) return 'ログインする'
  if (emailExists.value === false) return '会員登録する'
  return 'ログイン・会員登録する'
})

function applyBasicDataEmail(payload: unknown) {
  if (payload == null || typeof payload !== 'object') return
  const email = (payload as BasicData).email
  if (email && !mailLogin.email) {
    mailLogin.email = email
  }
}

function isInValidEmail(mailStr: string) {
  const reg =
    /^[A-Za-z0-9]{1}[A-Za-z0-9_.-]*@{1}[A-Za-z0-9_.-]{1,}\.[A-Za-z0-9]{1,}$/
  return !reg.test(mailStr)
}

function isValidPassword(password: string) {
  if (password.length < 8 || password.length > 20) return false
  const hasLowerCase = /[a-z]/.test(password)
  const hasUpperCase = /[A-Z]/.test(password)
  const hasNumber = /\d/.test(password)
  return hasLowerCase && hasUpperCase && hasNumber
}

const checkEmailExistsDebounced = useDebounceFn(async (email: string) => {
  const client = getClient()
  if (!client?.checkEmailExists) return
  const exists = await client.checkEmailExists(email)
  if (mailLogin.email === email) {
    emailExists.value = exists
  }
}, 400)

watch(
  () => mailLogin.email,
  (email) => {
    emailExists.value = null
    if (isInValidEmail(email)) return
    checkEmailExistsDebounced(email)
  }
)

function requireAgree() {
  if (isAgree.value) return true
  vm.$message({
    message: '利用規約と個人情報の扱いに同意してください。',
    type: 'error'
  })
  return false
}

async function submitIdPw() {
  if (!requireAgree()) return
  if (isInValidEmail(mailLogin.email)) {
    vm.$message({
      message: 'メールアドレスを正しく入力してください',
      type: 'error'
    })
    return
  }
  if (!mailLogin.passwd) {
    vm.$message({
      message: 'パスワードを入力してください',
      type: 'error'
    })
    return
  }

  const client = getClient()
  if (!client) {
    vm.$message({
      message: 'エラーが発生しました。もう一度お試しください。',
      type: 'error'
    })
    return
  }

  loginEmailSending.value = true
  try {
    const signInResult = await client.signInEmailPasswd(
      mailLogin.email,
      mailLogin.passwd,
      { signupFallback: true }
    )
    if (signInResult !== 'retry-as-signup') return

    if (emailExists.value === true) {
      vm.$message({
        message:
          'ログインに失敗しました。メールアドレスまたはパスワードが違います',
        type: 'error'
      })
      return
    }
    if (mailLogin.passwd !== mailLogin.passwdConfirm) {
      vm.$message({
        message: 'パスワードと確認用パスワードが一致していません。',
        type: 'error'
      })
      return
    }
    if (!isValidPassword(mailLogin.passwd)) {
      vm.$message({
        message:
          'パスワードを正しい形式で入力してください。※半角英小文字・大文字・数字を組合せて8〜20字で入力してください。記号も使用できます。',
        type: 'error'
      })
      return
    }
    await client.signOnEmailPasswd(mailLogin.email, mailLogin.passwd, {
      signInAlreadyTried: true
    })
  } catch (error) {
    console.error(error)
    vm.$message({
      message: 'エラーが発生しました。もう一度お試しください。',
      type: 'error'
    })
  } finally {
    loginEmailSending.value = false
  }
}

function signInGoogle() {
  if (!requireAgree()) return
  // signInWithRedirect はページ遷移を伴うため、認証完了後にクエリパラメータが
  // 残っている保証がない。遷移前に sessionStorage へ退避しておく。
  stashOfferChatRedirectUrl()
  getClient()?.signInGoogle()
}

function signInLine() {
  if (!requireAgree()) return
  // LINEログインのコールバックは現状 /mypage 固定で offerChatRedirectUrl を
  // 引き継げないが、コールバック側が対応した際に使えるよう退避だけしておく。
  stashOfferChatRedirectUrl()
  getClient()?.signInLine()
}

function signInApple() {
  if (!requireAgree()) return
  stashOfferChatRedirectUrl()
  getClient()?.signInApple()
}

function resetloginEmail() {
  mailLogin.email = ''
  loginEmailSending.value = false
  mailLoginForm.value?.resetFields()
}

function mailSubmit() {
  if (!requireAgree()) return
  mailLoginForm.value?.validate((valid) => {
    if (!valid) return
    getClient()?.signInEmail(mailLogin.email)
    loginEmailSending.value = true
  })
}

onMounted(() => {
  emitter.on(SING_IN_MAIL_SEND, resetloginEmail)
  emitter.on(SING_IN_MAIL_FAIL, resetloginEmail)
  emitter.on('basic-data-changed', applyBasicDataEmail)
  localforage.getItem<BasicData>('basicData').then((data) => {
    applyBasicDataEmail(data)
  })
})

onBeforeUnmount(() => {
  emitter.off(SING_IN_MAIL_SEND, resetloginEmail)
  emitter.off(SING_IN_MAIL_FAIL, resetloginEmail)
  emitter.off('basic-data-changed', applyBasicDataEmail)
})
</script>

<style lang="scss" scoped>
@import '@/assets/scss/auth/login-special-forms';
</style>
